/* ------------------------------------------------------------------------
   Quick Compare —  Used only by sections/quick-compare.liquid.
   ------------------------------------------------------------------------ */

/* pointer cursor move */
/* (px) a card drifts */
const QC_POINTER_SHIFT = 14;
/* pointer cursor move */

/* Custom scrollbar for a single */
class QuickCompareScrollbar {
  constructor(row, bar) {
    this.row = row;
    this.bar = bar;
    this.card = row.closest('.qc_card');
    this.track = bar.querySelector('[data-qc-track]');
    this.thumb = bar.querySelector('[data-qc-thumb]');
    this.raf = null;
    this.dragStartX = 0;
    this.dragStartScroll = 0;

    this.requestSync = this.requestSync.bind(this);
    this.onThumbDown = this.onThumbDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onTrackDown = this.onTrackDown.bind(this);
  }

  connect() {
    if (!this.track || !this.thumb) return;

    this.row.addEventListener('scroll', this.requestSync, { passive: true });
    window.addEventListener('resize', this.requestSync, { passive: true });
    this.thumb.addEventListener('pointerdown', this.onThumbDown);
    this.track.addEventListener('pointerdown', this.onTrackDown);

    this.sync();
  }

  disconnect() {
    this.row.removeEventListener('scroll', this.requestSync);
    window.removeEventListener('resize', this.requestSync);
    if (this.thumb) this.thumb.removeEventListener('pointerdown', this.onThumbDown);
    if (this.track) this.track.removeEventListener('pointerdown', this.onTrackDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  requestSync() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.sync();
    });
  }

  get maxScroll() {
    return this.row.scrollWidth - this.row.clientWidth;
  }

  sync() {
    const overflows = this.maxScroll > 1;

    this.bar.hidden = !overflows;
    if (this.card) this.card.classList.toggle('has-scrollbar', overflows);
    if (!overflows) return;

    const trackWidth = this.track.clientWidth;
    const ratio = this.row.clientWidth / this.row.scrollWidth;
    const thumbWidth = Math.max(trackWidth * ratio, 40);
    const progress = this.row.scrollLeft / this.maxScroll;

    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${progress * (trackWidth - thumbWidth)}px)`;
  }

  onThumbDown(event) {
    event.preventDefault();
    this.dragStartX = event.clientX;
    this.dragStartScroll = this.row.scrollLeft;
    this.bar.classList.add('is-dragging');

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  onPointerMove(event) {
    const trackWidth = this.track.clientWidth;
    const thumbWidth = this.thumb.offsetWidth;
    const scrollable = trackWidth - thumbWidth;
    if (scrollable <= 0) return;

    const delta = event.clientX - this.dragStartX;
    this.row.scrollLeft = this.dragStartScroll + (delta / scrollable) * this.maxScroll;
  }

  onPointerUp() {
    this.bar.classList.remove('is-dragging');
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }

  onTrackDown(event) {
    if (event.target === this.thumb) return;

    const rect = this.track.getBoundingClientRect();
    const thumbWidth = this.thumb.offsetWidth;
    const position = event.clientX - rect.left - thumbWidth / 2;
    const scrollable = rect.width - thumbWidth;
    if (scrollable <= 0) return;

    const progress = Math.min(Math.max(position / scrollable, 0), 1);
    this.row.scrollTo({ left: progress * this.maxScroll, behavior: 'smooth' });
  }
}

class QuickCompare extends HTMLElement {
  constructor() {
    super();

    this.cards = Array.from(this.querySelectorAll('.qc_card'));
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.raf = null;

    this.hasUserScrolled = false;

    this.updateCards = this.updateCards.bind(this);
    this.requestUpdate = this.requestUpdate.bind(this);
    this.handleMobileChange = this.handleMobileChange.bind(this);
    this.markUserScrolled = this.markUserScrolled.bind(this);
  }

  connectedCallback() {
    // Custom row scrollbars.
    this.scrollbars = this.cards
      .map((card) => {
        const row = card.querySelector('.qc_row');
        const bar = card.querySelector('[data-qc-scrollbar]');
        return row && bar ? new QuickCompareScrollbar(row, bar) : null;
      })
      .filter(Boolean);

    this.scrollbars.forEach((scrollbar) => scrollbar.connect());

    /* pointer cursor move  */
    this.setupPointerMove();
    /* pointer cursor move  */

    this.setupReveal();

    if (this.cards.length <= 1) return;

    this.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.requestUpdate, { passive: true });
    this.addEventListener('pointerdown', this.markUserScrolled, { passive: true });
    this.addEventListener('touchstart', this.markUserScrolled, { passive: true });
    this.addEventListener('wheel', this.markUserScrolled, { passive: true });

    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener('change', this.handleMobileChange);
    }

    this.updateCards();
    requestAnimationFrame(() => this.centerRecommended());
  }

  /* Each card show on scrolls */
  setupReveal() {
    if (!('IntersectionObserver' in window) || this.reducedMotionQuery.matches) return;

    this.cards.forEach((card) => card.classList.add('qc_card--will-reveal'));

    this.lastScrollY = window.scrollY;
    this.scrollingDown = true;

    this.trackScrollDirection = () => {
      const y = window.scrollY;
      if (y === this.lastScrollY) return;
      this.scrollingDown = y > this.lastScrollY;
      this.lastScrollY = y;
    };

    window.addEventListener('scroll', this.trackScrollDirection, { passive: true });

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        
        const batch = this.scrollingDown ? entries : entries.slice().reverse();

        batch.forEach((entry, order) => {
          const card = entry.target;
          card.style.setProperty('--qc-reveal-delay', `${order * 150}ms`);

          if (entry.isIntersecting) {
            card.classList.add('is-in-view');
            return;
          }

          
          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          const exitedBelow = entry.boundingClientRect.top > rootTop;

          card.style.setProperty('--qc-reveal-from', exitedBelow ? '5rem' : '-5rem');
          card.classList.remove('is-in-view');
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' },
    );

    this.cards.forEach((card) => this.revealObserver.observe(card));
  }

  /* pointer cursor move  */
  setupPointerMove() {
    this.pointerQuery = window.matchMedia('(min-width: 990px) and (pointer: fine)');

    this.pointerHandlers = this.cards.map((card) => {
      let frame = null;
      let pointerX = 0;
      let pointerY = 0;
      let shiftX = 0;
      let shiftY = 0;

      const canMove = () => this.pointerQuery.matches && !this.reducedMotionQuery.matches;

      const reset = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = null;
        shiftX = 0;
        shiftY = 0;
        card.classList.remove('is-pointer-move');
        card.style.setProperty('--qc-pointer-x', '0px');
        card.style.setProperty('--qc-pointer-y', '0px');
      };

      const onEnter = () => {
        if (canMove()) card.classList.add('is-pointer-move');
      };

      const onMove = (event) => {
        // Let the custom scrollbar drag own the pointer while it is active.
        if (!canMove() || card.querySelector('.qc_scrollbar.is-dragging')) return;

        pointerX = event.clientX;
        pointerY = event.clientY;

        if (frame) return;

        frame = requestAnimationFrame(() => {
          frame = null;

          const rect = card.getBoundingClientRect();
          if (!rect.width || !rect.height) return;

          const restLeft = rect.left - shiftX;
          const restTop = rect.top - shiftY;

          shiftX = ((pointerX - restLeft) / rect.width - 0.5) * QC_POINTER_SHIFT;
          shiftY = ((pointerY - restTop) / rect.height - 0.5) * QC_POINTER_SHIFT;

          card.style.setProperty('--qc-pointer-x', `${shiftX.toFixed(2)}px`);
          card.style.setProperty('--qc-pointer-y', `${shiftY.toFixed(2)}px`);
        });
      };

      card.addEventListener('pointerenter', onEnter);
      card.addEventListener('pointermove', onMove);
      card.addEventListener('pointerleave', reset);
      card.addEventListener('pointercancel', reset);

      return { card, onEnter, onMove, reset };
    });
  }

  teardownPointerMove() {
    if (!this.pointerHandlers) return;

    this.pointerHandlers.forEach(({ card, onEnter, onMove, reset }) => {
      card.removeEventListener('pointerenter', onEnter);
      card.removeEventListener('pointermove', onMove);
      card.removeEventListener('pointerleave', reset);
      card.removeEventListener('pointercancel', reset);
      reset();
    });

    this.pointerHandlers = null;
  }
  /* pointer cursor move  */

  disconnectedCallback() {
    if (this.scrollbars) this.scrollbars.forEach((scrollbar) => scrollbar.disconnect());

    this.teardownPointerMove();

    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.trackScrollDirection) window.removeEventListener('scroll', this.trackScrollDirection);

    this.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.requestUpdate);
    this.removeEventListener('pointerdown', this.markUserScrolled);
    this.removeEventListener('touchstart', this.markUserScrolled);
    this.removeEventListener('wheel', this.markUserScrolled);

    if (this.mobileQuery.removeEventListener) {
      this.mobileQuery.removeEventListener('change', this.handleMobileChange);
    }

    if (this.raf) cancelAnimationFrame(this.raf);
  }

  handleMobileChange() {
    this.updateCards();
    // Entering the mobile slider fresh land on the recommended card.
    if (this.mobileQuery.matches && !this.hasUserScrolled) {
      requestAnimationFrame(() => this.centerRecommended());
    }
  }

  markUserScrolled() {
    this.hasUserScrolled = true;
  }

  /* Centers the card inside the mobile. */
  centerRecommended() {
    if (!this.mobileQuery.matches || this.hasUserScrolled) return;

    const card = this.querySelector('.qc_card.is-recommended');
    if (!card) return;

    const trackRect = this.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const delta = cardRect.left + cardRect.width / 2 - (trackRect.left + trackRect.width / 2);
    if (Math.abs(delta) < 1) return;

    this.scrollLeft += delta;
    this.updateCards();
  }

  requestUpdate() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(this.updateCards);
  }

  updateCards() {
    this.raf = null;

    if (!this.mobileQuery.matches || this.reducedMotionQuery.matches) {
      this.cards.forEach((card) => {
        card.style.removeProperty('--qc-card-opacity');
        card.style.removeProperty('--qc-card-scale');
      });
      return;
    }

    const trackRect = this.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    const gap = parseFloat(getComputedStyle(this).columnGap) || 0;

    this.cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const span = rect.width + gap;
      const progress = span > 0 ? Math.min(1, Math.abs(center - cardCenter) / span) : 0;

      card.style.setProperty('--qc-card-opacity', (1 - progress * 0.5).toFixed(3));
      card.style.setProperty('--qc-card-scale', (1 - progress * 0.06).toFixed(4));
    });
  }
}

if (!customElements.get('quick-compare')) {
  customElements.define('quick-compare', QuickCompare);
}
